/**
 * Labstep
 *
 * @module grid/Index/components/ToolPanel/Columns
 * @desc Entity DataGrid dropdown to set column options
 */

import type { Column } from 'ag-grid-community';
import { useDataGridContext } from 'labstep-web/contexts/grid/hook';
import { toggleColumnVisible } from 'labstep-web/core/DataGrid/utils';
import { Icon } from 'labstep-web/core/Icon';
import { GridIndexToolPanelColumnsSection } from 'labstep-web/grid/Index/components/ToolPanel/Columns/Section';
import { SectionType } from 'labstep-web/grid/services/grid.service';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import styles from './styles.module.scss';
import type { GridIndexToolPanelColumnsProps } from './types';
import {
  filterHiddenColumnDefs,
  filterPinnedLeftColumnDefs,
  filterPinnedRightColumnDefs,
  filterShownAndNotPinnedPositionColumnDefs,
  getColIdArr,
} from './utils';

export const GridIndexToolPanelColumns: React.FC<
  GridIndexToolPanelColumnsProps
> = ({ extraColumnOptions }) => {
  const { agGrid, columns, columnState } = useDataGridContext();
  if (!columns) {
    return null;
  }
  /**
   * Columns by type (pinned left, pinned right, shown, hidden)
   */
  const columnsByType: Record<
    'pinnedLeft' | 'pinnedRight' | 'shown' | 'hidden',
    Column[]
  > = useMemo(
    () => ({
      pinnedLeft: filterPinnedLeftColumnDefs(columns, columnState),
      pinnedRight: filterPinnedRightColumnDefs(columns, columnState),
      shown: filterShownAndNotPinnedPositionColumnDefs(
        columns,
        columnState,
      ),
      hidden: filterHiddenColumnDefs(columns, columnState),
    }),
    [columns, columnState],
  );

  const onSortEnd = useCallback(
    (columnsSorted: Column[]) => {
      if (agGrid) {
        const allColumns = [
          ...columnsByType.pinnedLeft,
          ...columnsSorted,
          ...columnsByType.pinnedRight,
          ...columnsByType.hidden,
        ];
        const allColumnsColIdArr = getColIdArr(allColumns);
        agGrid.columnApi.applyColumnState({
          state: allColumnsColIdArr,
          applyOrder: true,
        });
      }
    },
    [agGrid],
  );

  const toggleColumnVisibleByColId = useCallback(
    (colId) => {
      if (agGrid) {
        toggleColumnVisible(agGrid, colId);
      }
    },
    [agGrid],
  );

  return (
    <div className={styles.container}>
      <Icon
        className={styles.close}
        name="times"
        onClick={() => agGrid?.api?.setSideBarVisible(false)}
      />
      <GridIndexToolPanelColumnsSection
        section={SectionType.visible}
        columns={columnsByType.shown}
        onToggleColumnVisible={toggleColumnVisibleByColId}
        onSortColumns={onSortEnd}
      />
      <GridIndexToolPanelColumnsSection
        section={SectionType.hidden}
        columns={columnsByType.hidden}
        onToggleColumnVisible={toggleColumnVisibleByColId}
        onSortColumns={onSortEnd}
      />
      {extraColumnOptions}
    </div>
  );
};
